import {
  type ColumnLayout,
  columnLayoutController,
  loadColumnLayout,
  localColumnLayoutStorage,
} from '@trm/ui-kit-admin';
import { useMemo } from 'react';
import { create } from 'zustand';
import { useKycReviewAssistFlag } from '~/features/kyc-review-assist/hooks/useKycReviewAssistFlag';
import {
  emptySelection,
  withTraderToggled,
  withTradersSelected,
} from '~/services/cases/selectionState';
import { type ParsedTraderRow, parseTraderCsv } from '~/utils/parseTraderCsv';

import { USERS_TABLE_ID, USER_COLUMNS, userColumns } from './userColumns';
import { buildUserFilter, commitsImmediately } from './userFilter';
import { DEFAULT_USER_SORT, type UserSort } from './userSorting';

export const MAX_BULK_IDS = 200;
export const SEARCH_DEBOUNCE_MS = 300;

export interface IdFilter {
  readonly label: string;
  readonly rows: readonly ParsedTraderRow[];
}

interface UsersPageState {
  search: string;
  committedSearch: string;
  idFilter: IdFilter | null;
  csvError: string | null;
  page: number;
  sort: UserSort;
  selectedIds: ReadonlySet<number>;
  selectionError: string | null;
  selectionVersion: number;
  columnLayout: ColumnLayout;
  filters: {
    setSearch: (value: string) => void;
    submitSearch: () => void;
    uploadCsv: (file: File) => Promise<void>;
    filterToIds: (ids: readonly number[], label: string) => void;
    clearIdFilter: () => void;
    setPage: (page: number) => void;
    setSort: (sort: UserSort) => void;
  };
  selection: {
    toggle: (userId: number) => void;
    setMany: (userIds: readonly number[], selected: boolean) => void;
    clear: () => void;
  };
  slideoutUserId: number | undefined;
  slideout: {
    open: (userId: number) => void;
    close: () => void;
  };
  openBulkAction: { id: string; ids: readonly number[]; run: number } | null;
  bulkAction: {
    open: (id: string) => void;
    close: () => void;
  };
  setColumnLayout: (layout: ColumnLayout) => void;
}

const limitedSelection = (selectedIds: ReadonlySet<number>, next: ReadonlySet<number>) =>
  next.size <= MAX_BULK_IDS
    ? { selectedIds: next, selectionError: null }
    : {
        selectedIds,
        selectionError: `Select no more than ${MAX_BULK_IDS} users for a bulk action.`,
      };

const clearedSelection = (state: { selectionVersion: number }) => ({
  selectedIds: emptySelection,
  selectionError: null,
  selectionVersion: state.selectionVersion + 1,
});

const timers = { commit: null as ReturnType<typeof setTimeout> | null };

export const useUsersPageStore = create<UsersPageState>((set) => {
  const restarted = (state: { selectionVersion: number }) => ({
    page: 1,
    ...clearedSelection(state),
  });

  return {
    search: '',
    committedSearch: '',
    idFilter: null,
    csvError: null,
    page: 1,
    sort: DEFAULT_USER_SORT,
    selectedIds: emptySelection,
    selectionError: null,
    selectionVersion: 0,
    columnLayout: loadColumnLayout(USERS_TABLE_ID, USER_COLUMNS, localColumnLayoutStorage),
    openBulkAction: null,
    slideoutUserId: undefined,
    slideout: {
      open: (userId) => set({ slideoutUserId: userId }),
      close: () => set({ slideoutUserId: undefined }),
    },
    filters: {
      setSearch: (value) => {
        if (timers.commit !== null) clearTimeout(timers.commit);
        if (commitsImmediately(value)) {
          timers.commit = setTimeout(
            () => set((state) => ({ committedSearch: value, ...restarted(state) })),
            SEARCH_DEBOUNCE_MS,
          );
        }
        set((state) => ({ search: value, ...restarted(state) }));
      },
      submitSearch: () => {
        if (timers.commit !== null) clearTimeout(timers.commit);
        set((state) =>
          commitsImmediately(state.search)
            ? { committedSearch: state.search, ...restarted(state) }
            : {},
        );
      },
      uploadCsv: async (file) => {
        const parsed = parseTraderCsv(await file.text());
        set((state) =>
          parsed.ok
            ? {
                idFilter: { rows: parsed.rows, label: file.name },
                csvError: null,
                ...restarted(state),
              }
            : { csvError: parsed.error, ...restarted(state) },
        );
      },
      filterToIds: (ids, label) =>
        set((state) => ({
          idFilter: { rows: ids.map((id) => ({ userId: String(id) })), label },
          csvError: null,
          ...restarted(state),
        })),
      clearIdFilter: () =>
        set((state) => ({ idFilter: null, csvError: null, ...restarted(state) })),
      setPage: (page) => set({ page }),
      setSort: (sort) => set((state) => ({ sort, ...restarted(state) })),
    },
    selection: {
      toggle: (userId) =>
        set((state) =>
          limitedSelection(state.selectedIds, withTraderToggled(state.selectedIds, userId)),
        ),
      setMany: (userIds, selected) =>
        set((state) =>
          limitedSelection(
            state.selectedIds,
            withTradersSelected(state.selectedIds, userIds, selected),
          ),
        ),
      clear: () => set(clearedSelection),
    },
    bulkAction: {
      open: (id) =>
        set((state) => ({
          openBulkAction: {
            id,
            ids: [...state.selectedIds],
            run: (state.openBulkAction?.run ?? 0) + 1,
          },
        })),
      close: () => set({ openBulkAction: null }),
    },
    setColumnLayout: (columnLayout) => {
      localColumnLayoutStorage.write(USERS_TABLE_ID, columnLayout);
      set({ columnLayout });
    },
  };
});

export const clearUsersSelection = (): void => useUsersPageStore.getState().selection.clear();

export const useUsersFiltersActions = () => useUsersPageStore((state) => state.filters);
export const useUsersSelectionActions = () => useUsersPageStore((state) => state.selection);
export const useSelectedUserIds = () => useUsersPageStore((state) => state.selectedIds);
export const useSelectionError = () => useUsersPageStore((state) => state.selectionError);
export const useOpenBulkAction = () => useUsersPageStore((state) => state.openBulkAction);
export const useUserSlideoutActions = () => useUsersPageStore((state) => state.slideout);
export const useSlideoutUserId = () => useUsersPageStore((state) => state.slideoutUserId);
export const useBulkActionControls = () => useUsersPageStore((state) => state.bulkAction);

export const useUsersFilter = () => {
  const committedSearch = useUsersPageStore((state) => state.committedSearch);
  const csvRows = useUsersPageStore((state) => state.idFilter?.rows ?? null);
  return useMemo(
    () => buildUserFilter({ search: committedSearch, csvRows }),
    [committedSearch, csvRows],
  );
};

export const useUserSort = () => useUsersPageStore((state) => state.sort);

export const useIsSearchPending = () =>
  useUsersPageStore(
    (state) => state.search !== state.committedSearch && commitsImmediately(state.search),
  );

export const useHasFilters = () =>
  useUsersPageStore((state) => state.search.trim() !== '' || state.idFilter !== null);

export const useUserColumnLayout = () => {
  const layout = useUsersPageStore((state) => state.columnLayout);
  const setColumnLayout = useUsersPageStore((state) => state.setColumnLayout);
  const { kycReviewAssistEnabled } = useKycReviewAssistFlag();
  return columnLayoutController(userColumns(kycReviewAssistEnabled), layout, setColumnLayout);
};
